"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlatformError } from "./client";
import { platformFetch, platformSend } from "./browser";

/* Money in and money out.

   `/bankrolls` publishes what the site accepts: one entry per token, each
   carrying its transfer routes. A route with a `fromToken` takes deposits, a
   route with a `toToken` pays withdrawals, and every limit and address belongs
   to a route rather than to the token — so nothing here is derived from the
   token alone.

   Both directions need a session. Neither invents a number: an address we made
   up would take a real deposit nowhere, and a minimum we guessed would be
   refused by the API after the player had already typed it. */

export type Network = {
  id: string;
  name: string;
  logoUrl: string;
  environment: string;
  attributes: { chainId?: number };
};

export type Token = {
  id: string;
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  significantDecimals: number;
  logoUrl: string;
  network: Network;
};

export type TransferRoute = {
  id: string;
  type: string;
  kind: string | null;
  fromToken?: Token;
  toToken?: Token;
};

export type Bankroll = {
  id: string;
  token: Token;
  transferRoutes?: TransferRoute[] | null;
  address: string;
  name: string;
};

/* One route, with the bankroll it moves against — the pair every panel needs,
   since limits come from the route and scale comes from the bankroll token. */
export type RouteOption = { route: TransferRoute; bankroll: Bankroll; token: Token };

export const EVM_ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

export const useBankrolls = (enabled: boolean) =>
  useQuery({
    queryKey: ["bankrolls"],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => (await platformFetch<{ data: Bankroll[] }>("bankrolls")).data,
  });

/* Stablemint is the site's fiat on-ramp, and it has its own `initiate`
   endpoints rather than an address or a destination form. Listing one of its
   routes beside the crypto routes would offer a player a flow these panels do
   not implement, so they are filtered out where they cannot be honoured. */
const isCrypto = (route: TransferRoute) => route.kind !== "stablemint";

export const routeOptions = (bankrolls: Bankroll[], direction: "deposit" | "withdraw"): RouteOption[] =>
  bankrolls.flatMap((bankroll) =>
    (bankroll.transferRoutes ?? [])
      .filter((route) => route.type === direction && isCrypto(route))
      .map((route) => ({
        route,
        bankroll,
        /* A deposit arrives in the route's `fromToken` and a withdrawal leaves
           in its `toToken`; they are the same token on a single-token site and
           are not on a bridged one. The bankroll token is the fallback only
           when the route names neither. */
        token: (direction === "deposit" ? route.fromToken : route.toToken) ?? bankroll.token,
      })),
  );

export const hasFilteredRoute = (bankrolls: Bankroll[], direction: "deposit" | "withdraw") =>
  bankrolls.some((bankroll) =>
    (bankroll.transferRoutes ?? []).some((route) => route.type === direction && !isCrypto(route)),
  );

/* Paying straight from a browser wallet needs a contract to call. A route
   whose token sits at the zero address is the network's own coin, which moves
   by a plain value transfer this app does not build — those routes still take
   a deposit sent by hand, they just cannot be paid from here. */
export const isWalletPayable = (option: RouteOption) =>
  option.token.network.environment === "evm" &&
  option.token.address !== EVM_ZERO_ADDRESS &&
  typeof option.token.network.attributes.chainId === "number";

export type DepositDetails = { minimumAmount: string; rate: string; address: string };
export type WithdrawDetails = { minimumAmount: string; rate: string; addressPattern: string };

export const useDepositDetails = (routeId: string | undefined) =>
  useQuery({
    queryKey: ["deposit", routeId],
    enabled: Boolean(routeId),
    /* The address is issued per account and route and does not rotate under
       us, but it is the one string a player pastes into a wallet — a stale
       read is not worth the risk of it having moved. */
    staleTime: 60_000,
    queryFn: async () =>
      (await platformFetch<{ data: DepositDetails }>(`deposit/${routeId}`)).data,
  });

export const useWithdrawDetails = (routeId: string | undefined) =>
  useQuery({
    queryKey: ["withdraw", routeId],
    enabled: Boolean(routeId),
    staleTime: 60_000,
    queryFn: async () =>
      (await platformFetch<{ data: WithdrawDetails }>(`withdraw/${routeId}`)).data,
  });

/* The API sees a deposit when it sees the transaction, and it does not see the
   transaction the instant the wallet returns a hash. So this retries until the
   platform accepts the hash or the window closes — the same shape the
   platform's own client uses, because a single call loses the deposits sent
   while the node was still catching up.

   A 401, 403 or 409 is final: not signed in, not allowed, or already
   submitted. Retrying those just spends the window. The cadence is the
   whitelabel's useDeposit: every 250ms for up to 30 seconds. */
const CONFIRM_WINDOW_MS = 30_000;
const CONFIRM_RETRY_MS = 250;
const FINAL_STATUSES = new Set([401, 403, 409]);

export type DepositConfirmation = {
  routeId: string;
  hash: string;
  sourceWalletAddress: string;
};

export const useConfirmDeposit = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({ routeId, hash, sourceWalletAddress }: DepositConfirmation) => {
      const deadline = Date.now() + CONFIRM_WINDOW_MS;
      let last: unknown;

      while (Date.now() < deadline) {
        try {
          return await platformSend<{ message: string }>("deposit", {
            siteBankrollTransferRouteId: routeId,
            hash,
            sourceWalletAddress,
          });
        } catch (error) {
          if (error instanceof PlatformError && FINAL_STATUSES.has(error.status)) {
            throw error;
          }

          last = error;
          await new Promise((resolve) => setTimeout(resolve, CONFIRM_RETRY_MS));
        }
      }

      throw last instanceof Error
        ? last
        : new PlatformError(504, "timeout", "The deposit was not confirmed in time.");
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["balances"] });
      client.invalidateQueries({ queryKey: ["account", "transfers"] });
    },
  });
};

export type WithdrawalRequest = { routeId: string; address: string; amount: bigint };

export const useRequestWithdrawal = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({ routeId, address, amount }: WithdrawalRequest) =>
      (
        await platformSend<{ data: { id: string } }>(`withdraw/${routeId}`, {
          address,
          /* Base units, as an integer string — the same scale every balance
             arrives in. Posting what the player typed is off by 10^decimals. */
          amount: amount.toString(),
        })
      ).data,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["balances"] });
      client.invalidateQueries({ queryKey: ["account", "transfers"] });
    },
  });
};

/* ---------------------------------------------------------------- fiat rails

   Stablemint is the site's fiat on- and off-ramp. Unlike the crypto routes
   above it has no address and no on-chain leg: the platform opens a hosted
   session and hands back a URL, and the player finishes on Stablemint's own
   pages. So there is no form to build here beyond an amount — the sensitive
   part never touches this origin.

   Two things differ from the crypto path and both are easy to get wrong:
   `amount` crosses the wire as a plain number in the balance's own currency,
   not as base units, and the three endpoints are distinct rather than one
   route with a flag. */

export type FiatMethod = "card" | "bank";

export const fiatRouteOptions = (
  bankrolls: Bankroll[],
  direction: "deposit" | "withdraw",
): RouteOption[] =>
  bankrolls.flatMap((bankroll) =>
    (bankroll.transferRoutes ?? [])
      .filter((route) => route.type === direction && !isCrypto(route))
      .map((route) => ({
        route,
        bankroll,
        token: (direction === "deposit" ? route.fromToken : route.toToken) ?? bankroll.token,
      })),
  );

export type FiatSession = { id: string; url: string; expiresAt: number; reference: string };

/* Where Stablemint sends the player back to: the whitelabel's
   `/fiat/{deposit|withdrawal}/{success|error|exit|support}` result pages, on
   this origin so a demo on its own subdomain returns to itself rather than to
   whichever host was configured when the site record was written. Stablemint
   accepts only https return URLs on a withdrawal, so that origin is forced to
   https there (a local http dev server still gets a usable session). */
export const fiatReturnUrls = (origin: string, type: "deposit" | "withdrawal") => {
  const base = type === "withdrawal" ? origin.replace(/^http:\/\//, "https://") : origin;

  return {
    successUrl: `${base}/fiat/${type}/success`,
    errorUrl: `${base}/fiat/${type}/error`,
    exitUrl: `${base}/fiat/${type}/exit`,
    supportUrl: `${base}/fiat/${type}/support`,
  };
};

export type FiatDepositRequest = { routeId: string; amount: number; method: FiatMethod };

export const useInitiateFiatDeposit = () =>
  useMutation({
    mutationFn: async ({ routeId, amount, method }: FiatDepositRequest) =>
      (
        await platformSend<{ data: FiatSession }>(
          method === "card"
            ? "deposit/integrations/stablemint/initiate-card"
            : "deposit/integrations/stablemint/initiate",
          {
            siteBankrollTransferRouteId: routeId,
            amount,
            /* A card payment runs in a frame on this page and takes only a
               support link; a bank transfer leaves the site and comes back. */
            ...(method === "card"
              ? { supportUrl: fiatReturnUrls(window.location.origin, "deposit").supportUrl }
              : fiatReturnUrls(window.location.origin, "deposit")),
          },
        )
      ).data,
  });

export type FiatWithdrawalRequest = { routeId: string; amount: number };

export const useInitiateFiatWithdrawal = () =>
  useMutation({
    mutationFn: async ({ routeId, amount }: FiatWithdrawalRequest) =>
      (
        await platformSend<{ data: FiatSession }>("withdraw/integrations/stablemint/initiate", {
          siteBankrollTransferRouteId: routeId,
          amount,
          ...fiatReturnUrls(window.location.origin, "withdrawal"),
        })
      ).data,
  });

/* A fiat amount is a currency figure, so it parses as a decimal number rather
   than through parseAmount's base-unit conversion. Rejects anything that is
   not a positive finite number so a typo cannot open a session for NaN. A
   comma is a decimal separator, as in every other cashier field. */
export const parseFiatAmount = (value: string): number | null => {
  const trimmed = value.trim().replace(/,/g, ".");

  if (!/^\d+(\.\d+)?$/.test(trimmed)) {
    return null;
  }

  const parsed = Number(trimmed);

  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/* The fiat ledger. Crypto transfers land in `/account/transfer-transactions`;
   Stablemint sessions do not — they have their own history, with their own
   statuses and a currency string instead of a token. Shown beside the crypto
   list rather than merged into it, because the two disagree about almost every
   field and a merge would invent a shape neither endpoint states. */
export type StablemintTransaction = {
  id: string;
  method: "bank" | "card";
  type: "deposit" | "withdrawal";
  status: "pending" | "completed" | "error" | "expired" | "failed" | "cancelled";
  errorReason: string | null;
  amount: string;
  currency: string;
  createdAt: string;
};

export const useStablemintTransactions = (enabled: boolean, { limit = 5 } = {}) =>
  useQuery({
    queryKey: ["stablemint-transactions", limit],
    enabled,
    staleTime: 30_000,
    queryFn: async () =>
      (
        await platformFetch<{ data: StablemintTransaction[] }>("stablemint-transactions", {
          limit,
        })
      ).data,
  });
